import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { t } from "../lib/i18n";
import { qk } from "./keys";
import type {
  HistoryEvent,
  ProjectCreateInput,
  ProjectDetail,
  ProjectFilters,
  ProjectPatchInput,
  ProjectStatus,
  ProjectStatusResult,
  ProjectSummary,
  StreamRoleKind,
  StreamTrackingInput,
} from "./types";

export function useProjects(filters: ProjectFilters) {
  return useQuery({
    queryKey: qk.projectList(filters),
    queryFn: ({ signal }) =>
      api<ProjectSummary[]>("/api/projects", {
        signal,
        query: {
          status: filters.status ?? "active",
          technology: filters.technology,
          trl_min: filters.trl_min,
          q: filters.q,
          mine: filters.mine ? true : undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });
}

export function useProject(slug: string | undefined) {
  return useQuery({
    queryKey: qk.project(slug ?? ""),
    queryFn: ({ signal }) => api<ProjectDetail>(`/api/projects/${encodeURIComponent(slug ?? "")}`, { signal }),
    enabled: Boolean(slug),
  });
}

export function useProjectHistory(slug: string, enabled = true) {
  return useQuery({
    queryKey: qk.projectHistory(slug),
    queryFn: ({ signal }) =>
      api<HistoryEvent[]>(`/api/projects/${encodeURIComponent(slug)}/history`, { signal }),
    enabled,
  });
}

export function useTechnologies(q: string, enabled = true) {
  return useQuery({
    queryKey: qk.technologies(q),
    queryFn: ({ signal }) => api<string[]>("/api/technologies", { signal, query: { q } }),
    enabled,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Everything a change on one project can show up in. */
export function invalidateProject(client: QueryClient, slug?: string) {
  void client.invalidateQueries({ queryKey: qk.projects });
  void client.invalidateQueries({ queryKey: slug ? qk.project(slug) : qk.projectAny });
  void client.invalidateQueries({ queryKey: qk.me });
}

/** Write endpoints answer with the fresh detail: put it in the cache instead of refetching. */
function useDetailMutation<TInput>(
  slug: string,
  request: (input: TInput) => Promise<ProjectDetail>,
  errorTitle: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: request,
    meta: { errorTitle },
    onSuccess: (detail) => {
      client.setQueryData(qk.project(slug), detail);
      void client.invalidateQueries({ queryKey: qk.projects });
      void client.invalidateQueries({ queryKey: qk.projectHistory(slug) });
    },
  });
}

export function useCreateProject() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectCreateInput) => api<ProjectDetail>("/api/projects", { body: input }),
    meta: { errorTitle: t("Project not created") },
    onSuccess: (detail) => {
      client.setQueryData(qk.project(detail.slug), detail);
      void client.invalidateQueries({ queryKey: qk.projects });
      void client.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function usePatchProject(slug: string) {
  return useDetailMutation(
    slug,
    (input: ProjectPatchInput) =>
      api<ProjectDetail>(`/api/projects/${encodeURIComponent(slug)}`, { method: "PATCH", body: input }),
    t("Project not updated"),
  );
}

export function useSetProjectRoles(slug: string) {
  return useDetailMutation(
    slug,
    (input: { tech_leads: number[]; product_owners: number[] }) =>
      api<ProjectDetail>(`/api/projects/${encodeURIComponent(slug)}/roles`, { method: "PUT", body: input }),
    t("TL/PO not saved"),
  );
}

export function useSetLinks(slug: string) {
  return useDetailMutation(
    slug,
    (links: Array<{ url: string; label: string; stream_id: number | null }>) =>
      api<ProjectDetail>(`/api/projects/${encodeURIComponent(slug)}/links`, { method: "PUT", body: { links } }),
    t("Links not saved"),
  );
}

export function useTrackStream(slug: string) {
  return useDetailMutation(
    slug,
    ({ id, ...input }: StreamTrackingInput & { id: number }) =>
      api<ProjectDetail>(`/api/streams/${id}/tracking`, { method: "PATCH", body: input }),
    t("Stream tracking not saved"),
  );
}

export function useSetStreamRoles(slug: string) {
  return useDetailMutation(
    slug,
    ({ id, ...roles }: Partial<Record<StreamRoleKind, number[]>> & { id: number }) =>
      api<ProjectDetail>(`/api/streams/${id}/roles`, { method: "PUT", body: roles }),
    t("Stream roles not saved"),
  );
}

export function useAddStream(slug: string) {
  return useDetailMutation(
    slug,
    (input: { title: string; description: string }) =>
      api<ProjectDetail>(`/api/projects/${encodeURIComponent(slug)}/streams`, { body: input }),
    t("Stream not created"),
  );
}

export function usePatchStream(slug: string) {
  return useDetailMutation(
    slug,
    ({ id, ...input }: { id: number; title?: string; description?: string }) =>
      api<ProjectDetail>(`/api/streams/${id}`, { method: "PATCH", body: input }),
    t("Stream not updated"),
  );
}

export function useArchiveStream(slug: string) {
  return useDetailMutation(
    slug,
    (id: number) => api<ProjectDetail>(`/api/streams/${id}/archive`, { method: "POST" }),
    t("Stream not archived"),
  );
}

export function useSetProjectStatus(slug: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (status: ProjectStatus) =>
      api<ProjectStatusResult>(`/api/projects/${encodeURIComponent(slug)}/status`, { body: { status } }),
    meta: { errorTitle: t("Status not changed") },
    onSuccess: () => {
      invalidateProject(client, slug);
      void client.invalidateQueries({ queryKey: qk.requests });
      void client.invalidateQueries({ queryKey: qk.admin });
    },
  });
}
